import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Typography, IconButton, Card } from "@mui/material";
import {
  CustomSectionTitle,
  CustomCheckboxToggle,
  CustomButton,
  CustomTextInput,
  CustomMiddleSelect,
  CustomLoading,
  PropertyRegistrationTabs,
  colors,
} from "@ippon/shared-components";
import { ButtonDeleteIcon } from "@ippon/shared-assets";
import PersonIcon from "@mui/icons-material/PersonOutline";
import BusinessIcon from "@mui/icons-material/Business";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch, RootState } from "@/store";
import {
  fetchDocumentManagement,
  upsertDocumentManagement,
  type DocumentDefinition,
  type DocumentsMap,
} from "@/store/documentManagementSlice";
import InitialLoadError from "@/components/ErrorState/InitialLoadError";
import type { ApiErrorPayload, ApiErrorType } from "@/utils/apiErrorPayload";
import {
  shouldShowInitialLoadError,
  useStaleInitialLoadErrorToast,
} from "@/utils/initialLoadError";
import PropertyDocumentSection from "./PropertyDocumentSection";
import { Can } from "@/components/auth/Can";
import { useCan } from "@/hooks/useCan";

const DOCUMENT_SETTING_TABS = [
  { id: 0, label: "契約書類" },
  { id: 1, label: "物件書類" },
];

export const SystemSetting: React.FC = () => {
  // （問い合わせ種類のUIは現在未使用）

  // 契約書類設定（個人/法人）
  type DocumentType = {
    id: string;
    name: string;
    enabled: boolean;
    isDefault: boolean;
    required?: boolean;
    order?: number;
  };
  const [activeTab, setActiveTab] = useState<number>(0); // 0: 契約書類, 1: 物件書類
  const [activeDocTab, setActiveDocTab] = useState<number>(0); // 0: 個人, 1: 法人
  const [individualDocuments, setIndividualDocuments] = useState<
    DocumentType[]
  >([]);
  const [corporateDocuments, setCorporateDocuments] = useState<DocumentType[]>(
    [],
  );
  const [isDataLoaded, setIsDataLoaded] = useState<boolean>(false);
  const dispatch = useDispatch<AppDispatch>();
  const can = useCan();
  const { record } = useSelector(
    (state: RootState) => state.documentManagement,
  );
  const {
    register,
    watch,
    setValue,
    formState: { errors },
  } = useForm<{ newIndividual: string; newCorporate: string }>({
    defaultValues: { newIndividual: "", newCorporate: "" },
  });
  const [isAddingIndividual, setIsAddingIndividual] = useState<boolean>(false);
  const [isAddingCorporate, setIsAddingCorporate] = useState<boolean>(false);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [panelHeight, setPanelHeight] = useState<number | null>(null);
  const [initialLoadError, setInitialLoadError] = useState<ApiErrorType | null>(
    null,
  );
  const hasCachedDocumentData =
    isDataLoaded ||
    !!record ||
    individualDocuments.length > 0 ||
    corporateDocuments.length > 0;
  const hasCachedDocumentDataRef = useRef(hasCachedDocumentData);

  useEffect(() => {
    hasCachedDocumentDataRef.current = hasCachedDocumentData;
  }, [hasCachedDocumentData]);

  // カスタム設定と同様、タブカードの背景を画面下端まで満たす。
  useEffect(() => {
    const measure = () => {
      const element = wrapRef.current;
      if (!element) return;
      setPanelHeight(
        Math.max(0, window.innerHeight - element.getBoundingClientRect().top),
      );
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  // データ取得
  const fetchDetail = useCallback(async () => {
    setInitialLoadError(null);
    if (!hasCachedDocumentDataRef.current) {
      setIsDataLoaded(false);
    }
    try {
      await dispatch(fetchDocumentManagement()).unwrap();
    } catch (err) {
      const payload = err as ApiErrorPayload | undefined;
      const errorType = payload?.errorType ?? "unknown";
      if (errorType !== "notFound" && hasCachedDocumentDataRef.current) {
        setIsDataLoaded(true);
      }
      setInitialLoadError(errorType);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  // 取得データ→UI反映
  useEffect(() => {
    if (!record) return;
    const toUi = (defs: DocumentDefinition[]): DocumentType[] =>
      [...defs]
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map((d) => ({
          id: d.document_type,
          name: d.display_name,
          enabled: d.is_enabled,
          isDefault: d.is_default,
          required: d.is_required,
          order: d.order,
        }));
    setIndividualDocuments(toUi(record.documents.individual));
    setCorporateDocuments(toUi(record.documents.corporate));
    setIsDataLoaded(true);
  }, [record]);

  const buildDocumentsMapFrom = (
    nextIndividual: DocumentType[],
    nextCorporate: DocumentType[],
  ): DocumentsMap => {
    const toDef = (list: DocumentType[]): DocumentDefinition[] =>
      list.map((d, idx) => ({
        document_type: d.id,
        display_name: d.name,
        is_required: d.required ?? d.isDefault ?? false,
        is_enabled: d.enabled,
        is_default: d.isDefault,
        order: idx + 1, // 常に現在の配列順序を反映
      }));
    return {
      individual: toDef(nextIndividual),
      corporate: toDef(nextCorporate),
    };
  };

  const saveNow = async (
    nextIndividual: DocumentType[],
    nextCorporate: DocumentType[],
  ) => {
    const docs = buildDocumentsMapFrom(nextIndividual, nextCorporate);
    await dispatch(
      upsertDocumentManagement({
        existingId: record?.document_management_id,
        documents: docs,
      }) as any,
    );
  };

  // —— 契約書類設定 ——

  useStaleInitialLoadErrorToast({
    errorType: initialLoadError,
    hasCachedData: hasCachedDocumentData,
    dedupeKey: "system-setting",
  });

  if (shouldShowInitialLoadError(initialLoadError, hasCachedDocumentData)) {
    return (
      <InitialLoadError errorType={initialLoadError} onRetry={fetchDetail} />
    );
  }

  return (
    <Box pt={{ xs: 2, sm: 3 }}>
      <CustomSectionTitle title="書類設定" addBorder={true} size="large" />
      <Box
        ref={wrapRef}
        sx={{
          backgroundColor: "#f5f5f5",
          ml: -3,
          mr: -3,
          pt: 1.7,
          px: 2,
          height: panelHeight ? `${panelHeight}px` : "auto",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box sx={{ height: "calc(100% - 24px)", minHeight: 0, width: "100%" }}>
          <PropertyRegistrationTabs
            currentTab={activeTab}
            tabs={DOCUMENT_SETTING_TABS}
            onTabChange={setActiveTab}
            disableConfirm
            hasFixedActionBar={activeTab === 1}
            scrollableContent
          >
            {activeTab === 1 ? (
              <PropertyDocumentSection />
            ) : (
              /* 契約書類設定（個人/法人） */
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    mb: 2,
                    mt: 1,
                    color: "#666",
                    fontSize: { xs: 12, sm: 14 },
                    lineHeight: 1.6,
                  }}
                >
                  契約者ごとに表示する書類を選択・作成してください。法人と個人でそれぞれ設定できます。
                </Typography>
                {!isDataLoaded ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      minHeight: "200px",
                      width: "100%",
                    }}
                  >
                    <CustomLoading
                      loading={true}
                      fullScreen={false}
                      size="medium"
                    />
                  </Box>
                ) : (
                  <Box sx={{ width: { xs: "100%", md: "60%" } }}>
                    {/* セグメント切替（個人／法人） */}
                    <CustomMiddleSelect
                      // label="対象"
                      options={[
                        {
                          value: "individual",
                          label: "個人",
                          icon: <PersonIcon sx={{ fontSize: 18 }} />,
                        },
                        {
                          value: "corporate",
                          label: "法人",
                          icon: <BusinessIcon sx={{ fontSize: 18 }} />,
                        },
                      ]}
                      value={activeDocTab === 0 ? "individual" : "corporate"}
                      onChange={(val) =>
                        setActiveDocTab(val === "individual" ? 0 : 1)
                      }
                      width={"100%"}
                    />

                    {/* リスト表示（全体カードで囲む） */}
                    <Card
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        boxShadow: 0,
                        border: "1px solid #e5e7eb",
                      }}
                    >
                      <Box sx={{ position: "relative", overflow: "hidden" }}>
                        <Box
                          sx={{
                            display: "flex",
                            transform: `translateX(-${activeDocTab * 100}%)`,
                          }}
                        >
                          {/* 個人タブのコンテンツ */}
                          <Box sx={{ flex: "0 0 100%", minWidth: 0 }}>
                            {individualDocuments.map((doc) => (
                              <Card
                                key={doc.id}
                                sx={{
                                  py: { xs: 1, sm: 1.5 },
                                  pl: { xs: 1.5, sm: 2 },
                                  pr: { xs: 1.5, sm: 2 },
                                  mb: 1.5,
                                  boxShadow: "none",
                                  border: "1px solid #e5e7eb",
                                  borderRadius: 2,
                                  borderTop:
                                    dragOverId === doc.id
                                      ? "2px solid #0B9DBD"
                                      : "1px solid #e5e7eb",
                                }}
                                draggable
                                onDragStart={() => setDraggingId(doc.id)}
                                onDragEnter={() => setDragOverId(doc.id)}
                                onDragOver={(e) => e.preventDefault()}
                                onDragEnd={() => {
                                  setDraggingId(null);
                                  setDragOverId(null);
                                }}
                                onDrop={() => {
                                  if (!draggingId || draggingId === doc.id)
                                    return;
                                  const reorder = (list: DocumentType[]) => {
                                    const fromIndex = list.findIndex(
                                      (d) => d.id === draggingId,
                                    );
                                    const toIndex = list.findIndex(
                                      (d) => d.id === doc.id,
                                    );
                                    if (fromIndex === -1 || toIndex === -1)
                                      return list;
                                    const newList = [...list];
                                    const [moved] = newList.splice(
                                      fromIndex,
                                      1,
                                    );
                                    newList.splice(toIndex, 0, moved);
                                    // 並び替え後に order を更新
                                    return newList.map((item, idx) => ({
                                      ...item,
                                      order: idx + 1,
                                    }));
                                  };
                                  if (activeDocTab === 0) {
                                    setIndividualDocuments((prev) => {
                                      const next = reorder(prev);
                                      saveNow(next, corporateDocuments);
                                      return next;
                                    });
                                  } else {
                                    setCorporateDocuments((prev) => {
                                      const next = reorder(prev);
                                      saveNow(individualDocuments, next);
                                      return next;
                                    });
                                  }
                                  setDraggingId(null);
                                  setDragOverId(null);
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    flexWrap: { xs: "wrap", sm: "nowrap" },
                                    gap: { xs: 1, sm: 0 },
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1.5,
                                      flex: { xs: "1 1 100%", sm: "auto" },
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        minWidth: { xs: "auto", sm: 180 },
                                        fontSize: { xs: 13, sm: 14 },
                                        fontWeight: 600,
                                      }}
                                    >
                                      {doc.name}
                                    </Typography>
                                    {doc.isDefault && (
                                      <Box
                                        sx={{
                                          fontSize: 10,
                                          px: 1,
                                          py: 0.25,
                                          borderRadius: 1,
                                          color: "#0B9DBD",
                                          backgroundColor: "#0B9DBD1A",
                                        }}
                                      >
                                        デフォルト
                                      </Box>
                                    )}
                                  </Box>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1.5,
                                      mr: 0,
                                    }}
                                  >
                                    {/* 削除ボタン（非デフォルトのみ表示） */}
                                    {!doc.isDefault && can("master:delete") && (
                                      <IconButton
                                        aria-label="delete"
                                        size="small"
                                        onClick={() => {
                                          if (activeDocTab === 0) {
                                            setIndividualDocuments((prev) => {
                                              const next = prev.filter(
                                                (d) => d.id !== doc.id,
                                              );
                                              saveNow(next, corporateDocuments);
                                              return next;
                                            });
                                          } else {
                                            setCorporateDocuments((prev) => {
                                              const next = prev.filter(
                                                (d) => d.id !== doc.id,
                                              );
                                              saveNow(
                                                individualDocuments,
                                                next,
                                              );
                                              return next;
                                            });
                                          }
                                        }}
                                        sx={{
                                          color: colors.text.secondary,
                                          "&:hover": {
                                            color: colors.status.error,
                                          },
                                        }}
                                      >
                                        <ButtonDeleteIcon
                                          width={14}
                                          height={16}
                                        />
                                      </IconButton>
                                    )}
                                    {/* トグル */}
                                    <Box
                                      sx={{
                                        transform: "scale(0.85)",
                                        transformOrigin: "right center",
                                        alignSelf: "flex-end",
                                        right: 0,
                                      }}
                                    >
                                      <CustomCheckboxToggle
                                        fieldName={doc.id}
                                        value={doc.enabled}
                                        onChange={() => {
                                          if (activeDocTab === 0) {
                                            setIndividualDocuments((prev) => {
                                              const next = prev.map((d) =>
                                                d.id === doc.id
                                                  ? {
                                                      ...d,
                                                      enabled: !d.enabled,
                                                    }
                                                  : d,
                                              );
                                              saveNow(next, corporateDocuments);
                                              return next;
                                            });
                                          } else {
                                            setCorporateDocuments((prev) => {
                                              const next = prev.map((d) =>
                                                d.id === doc.id
                                                  ? {
                                                      ...d,
                                                      enabled: !d.enabled,
                                                    }
                                                  : d,
                                              );
                                              saveNow(
                                                individualDocuments,
                                                next,
                                              );
                                              return next;
                                            });
                                          }
                                        }}
                                        variant="ios"
                                      />
                                    </Box>
                                  </Box>
                                </Box>
                              </Card>
                            ))}
                            {isAddingIndividual ? (
                              <Card
                                sx={{
                                  py: { xs: 1.5, sm: 0 },
                                  pl: { xs: 1.5, sm: 2 },
                                  pr: { xs: 1.5, sm: 2 },
                                  backgroundColor: "#F7FBFD",
                                  border: "1px dashed #0B9DBD4D",
                                  boxShadow: "none",
                                  borderRadius: 2,
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: { xs: 1, sm: 1.5 },
                                    width: "100%",
                                    flexWrap: { xs: "wrap", sm: "nowrap" },
                                  }}
                                >
                                  <Box
                                    sx={{
                                      flex: { xs: "1 1 100%", sm: 1 },
                                      mt: 2,
                                    }}
                                  >
                                    <CustomTextInput
                                      label=""
                                      name="newIndividual"
                                      placeholder="新しい書類を入力"
                                      register={register}
                                      errors={errors}
                                      inputWidth="100%"
                                      search={true}
                                    />
                                  </Box>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      gap: 1,
                                      width: { xs: "100%", sm: "auto" },
                                    }}
                                  >
                                    <Can action="master:edit">
                                      <CustomButton
                                        label="追加"
                                        sx={{
                                          width: { xs: "50%", sm: "80px" },
                                          minWidth: { xs: "auto", sm: "80px" },
                                          height: "36px !important",
                                        }}
                                        onClick={() => {
                                          const name =
                                            watch("newIndividual")?.trim();
                                          if (!name) return;
                                          const newDoc: DocumentType = {
                                            id: `ind-${Date.now()}`,
                                            name,
                                            enabled: true,
                                            isDefault: false,
                                          };
                                          setIndividualDocuments((prev) => {
                                            const next = [...prev, newDoc];
                                            saveNow(next, corporateDocuments);
                                            return next;
                                          });
                                          setValue("newIndividual", "");
                                          setIsAddingIndividual(false);
                                        }}
                                      />
                                    </Can>
                                    <CustomButton
                                      label="戻る"
                                      buttonCategory="cancel"
                                      sx={{
                                        width: { xs: "50%", sm: "80px" },
                                        minWidth: { xs: "auto", sm: "80px" },
                                        height: "38px !important",
                                      }}
                                      onClick={() => {
                                        setIsAddingIndividual(false);
                                        setValue("newIndividual", "");
                                      }}
                                    />
                                  </Box>
                                </Box>
                              </Card>
                            ) : (
                              <Box sx={{ mt: 2 }}>
                                <Can action="master:edit">
                                  <CustomButton
                                    label="新しい項目を追加"
                                    buttonCategory="clearPrimary"
                                    onClick={() => setIsAddingIndividual(true)}
                                    sx={{ width: "100%", borderStyle: "dashed" }}
                                  />
                                </Can>
                              </Box>
                            )}
                          </Box>

                          {/* 法人タブのコンテンツ */}
                          <Box sx={{ flex: "0 0 100%", minWidth: 0 }}>
                            {corporateDocuments.map((doc) => (
                              <Card
                                key={doc.id}
                                sx={{
                                  py: { xs: 1, sm: 1.5 },
                                  pl: { xs: 1.5, sm: 2 },
                                  pr: { xs: 1.5, sm: 2 },
                                  mb: 1.5,
                                  boxShadow: "none",
                                  border: "1px solid #e5e7eb",
                                  borderRadius: 2,
                                  borderTop:
                                    dragOverId === doc.id
                                      ? "2px solid #0B9DBD"
                                      : "1px solid #e5e7eb",
                                }}
                                draggable
                                onDragStart={() => setDraggingId(doc.id)}
                                onDragEnter={() => setDragOverId(doc.id)}
                                onDragOver={(e) => e.preventDefault()}
                                onDragEnd={() => {
                                  setDraggingId(null);
                                  setDragOverId(null);
                                }}
                                onDrop={() => {
                                  if (!draggingId || draggingId === doc.id)
                                    return;
                                  const reorder = (list: DocumentType[]) => {
                                    const fromIndex = list.findIndex(
                                      (d) => d.id === draggingId,
                                    );
                                    const toIndex = list.findIndex(
                                      (d) => d.id === doc.id,
                                    );
                                    if (fromIndex === -1 || toIndex === -1)
                                      return list;
                                    const newList = [...list];
                                    const [moved] = newList.splice(
                                      fromIndex,
                                      1,
                                    );
                                    newList.splice(toIndex, 0, moved);
                                    return newList.map((item, idx) => ({
                                      ...item,
                                      order: idx + 1,
                                    }));
                                  };
                                  setCorporateDocuments((prev) => {
                                    const next = reorder(prev);
                                    saveNow(individualDocuments, next);
                                    return next;
                                  });
                                  setDraggingId(null);
                                  setDragOverId(null);
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    flexWrap: { xs: "wrap", sm: "nowrap" },
                                    gap: { xs: 1, sm: 0 },
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1.5,
                                      flex: { xs: "1 1 100%", sm: "auto" },
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        minWidth: { xs: "auto", sm: 180 },
                                        fontSize: { xs: 13, sm: 14 },
                                        fontWeight: 600,
                                      }}
                                    >
                                      {doc.name}
                                    </Typography>
                                    {doc.isDefault && (
                                      <Box
                                        sx={{
                                          fontSize: 10,
                                          px: 1,
                                          py: 0.25,
                                          borderRadius: 1,
                                          color: "#0B9DBD",
                                          backgroundColor: "#0B9DBD1A",
                                        }}
                                      >
                                        デフォルト
                                      </Box>
                                    )}
                                  </Box>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1.5,
                                      mr: 0,
                                    }}
                                  >
                                    {!doc.isDefault && can("master:delete") && (
                                      <IconButton
                                        aria-label="delete"
                                        size="small"
                                        onClick={() => {
                                          setCorporateDocuments((prev) => {
                                            const next = prev.filter(
                                              (d) => d.id !== doc.id,
                                            );
                                            saveNow(individualDocuments, next);
                                            return next;
                                          });
                                        }}
                                        sx={{
                                          color: colors.text.secondary,
                                          "&:hover": {
                                            color: colors.status.error,
                                          },
                                        }}
                                      >
                                        <ButtonDeleteIcon
                                          width={14}
                                          height={16}
                                        />
                                      </IconButton>
                                    )}
                                    <Box
                                      sx={{
                                        transform: "scale(0.85)",
                                        transformOrigin: "right center",
                                        alignSelf: "flex-end",
                                        right: 0,
                                      }}
                                    >
                                      <CustomCheckboxToggle
                                        fieldName={doc.id}
                                        value={doc.enabled}
                                        onChange={() => {
                                          setCorporateDocuments((prev) => {
                                            const next = prev.map((d) =>
                                              d.id === doc.id
                                                ? { ...d, enabled: !d.enabled }
                                                : d,
                                            );
                                            saveNow(individualDocuments, next);
                                            return next;
                                          });
                                        }}
                                        variant="ios"
                                      />
                                    </Box>
                                  </Box>
                                </Box>
                              </Card>
                            ))}
                            {isAddingCorporate ? (
                              <Card
                                sx={{
                                  py: { xs: 1.5, sm: 2 },
                                  pl: { xs: 1.5, sm: 2 },
                                  pr: { xs: 1.5, sm: 2 },
                                  backgroundColor: "#F7FBFD",
                                  border: "1px dashed #0B9DBD4D",
                                  boxShadow: "none",
                                  borderRadius: 2,
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: { xs: 1, sm: 1.5 },
                                    width: "100%",
                                    flexWrap: { xs: "wrap", sm: "nowrap" },
                                  }}
                                >
                                  <Box
                                    sx={{
                                      flex: { xs: "1 1 100%", sm: 1 },
                                      mt: 1.5,
                                    }}
                                  >
                                    <CustomTextInput
                                      label=""
                                      name="newCorporate"
                                      placeholder="新しい書類を入力"
                                      register={register}
                                      errors={errors}
                                      inputWidth="100%"
                                      search={true}
                                    />
                                  </Box>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      gap: 1,
                                      width: { xs: "100%", sm: "auto" },
                                    }}
                                  >
                                    <Can action="master:edit">
                                      <CustomButton
                                        label="追加"
                                        sx={{
                                          width: { xs: "50%", sm: "80px" },
                                          minWidth: { xs: "auto", sm: "80px" },
                                          height: "36px !important",
                                        }}
                                        onClick={() => {
                                          const name =
                                            watch("newCorporate")?.trim();
                                          if (!name) return;
                                          const newDoc: DocumentType = {
                                            id: `corp-${Date.now()}`,
                                            name,
                                            enabled: true,
                                            isDefault: false,
                                          };
                                          setCorporateDocuments((prev) => {
                                            const next = [...prev, newDoc];
                                            saveNow(individualDocuments, next);
                                            return next;
                                          });
                                          setValue("newCorporate", "");
                                          setIsAddingCorporate(false);
                                        }}
                                      />
                                    </Can>
                                    <CustomButton
                                      label="戻る"
                                      buttonCategory="cancel"
                                      sx={{
                                        width: { xs: "50%", sm: "80px" },
                                        minWidth: { xs: "auto", sm: "80px" },
                                        height: "38px !important",
                                      }}
                                      onClick={() => {
                                        setIsAddingCorporate(false);
                                        setValue("newCorporate", "");
                                      }}
                                    />
                                  </Box>
                                </Box>
                              </Card>
                            ) : (
                              <Box sx={{ mt: 2 }}>
                                <Can action="master:edit">
                                  <CustomButton
                                    label="新しい項目を追加"
                                    buttonCategory="clearPrimary"
                                    onClick={() => setIsAddingCorporate(true)}
                                    sx={{ width: "100%", borderStyle: "dashed" }}
                                  />
                                </Can>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    </Card>
                  </Box>
                )}
              </Box>
            )}
          </PropertyRegistrationTabs>
        </Box>
      </Box>
    </Box>
  );
};

export default SystemSetting;
